import type { Locale } from '@porotutu/i18n'
import { DataTable } from '@porotutu/react/components/DataTable/DataTable'
import { EmptyState } from '@porotutu/react/components/EmptyState/EmptyState'
import { FilterBar } from '@porotutu/react/components/FilterBar/FilterBar'
import { Pagination } from '@porotutu/react/components/Pagination/Pagination'
import { Panel } from '@porotutu/react/components/Panel/Panel'
import { useT } from '@porotutu/react/session'
import type { AuditListQuery, EntryPage } from '../queries'
import { columnsFor } from './columns'
import { AuditExport } from './Export'
import { filtersFor, textsFor } from './filters'

export type AuditListProps = {
  page: EntryPage
  query: AuditListQuery
  locale: Locale
  onQuery: (patch: Record<string, string | number | undefined>) => void
  onReset: () => void
}

export function AuditList({ page, query, locale, onQuery, onReset }: AuditListProps) {
  const t = useT()
  const filtered =
    query.search !== undefined ||
    query.kind !== undefined ||
    query.action !== undefined ||
    query.subject_id !== undefined

  return (
    <Panel
      level={1}
      title={t('adm.audit.index.heading')}
      subtitle={t('adm.audit.index.subheading')}
      actions={<AuditExport query={query} locale={locale} />}
    >
      <FilterBar
        search={query.search ?? ''}
        searchPlaceholder={t('adm.audit.filters.search_placeholder')}
        selects={filtersFor(t, query)}
        texts={textsFor(t, query)}
        onChange={(patch) => onQuery({ ...patch, offset: undefined })}
        onReset={onReset}
      />

      <DataTable
        id="audit-list"
        columns={columnsFor(t, locale, query)}
        rows={page.data}
        rowKey={(row) => row.id}
        meta={page.meta}
        empty={
          <EmptyState>
            {filtered ? t('adm.audit.index.filtered_empty') : t('adm.audit.index.empty')}
          </EmptyState>
        }
      />

      <Pagination meta={page.meta} />
    </Panel>
  )
}
